import { useState, useEffect } from 'react'
import Button from '../../../components/common/Button/Button'
import { getCategoryOptions, getPriorityOptions } from '../../../services/announcementsService'

/**
 * AnnouncementModal Component
 * Modal form for creating/editing announcements
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close modal callback
 * @param {function} onSubmit - Submit form callback
 * @param {object} announcement - Existing announcement data (for edit mode)
 * @param {boolean} isLoading - Whether form is submitting
 */
const AnnouncementModal = ({ isOpen, onClose, onSubmit, announcement = null, isLoading = false }) => {
  // Form state
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [category, setCategory] = useState('General')
  const [priority, setPriority] = useState('Normal')
  const [error, setError] = useState('')

  // Character counts
  const titleMaxLength = 200
  const contentMaxLength = 2000

  // Load existing announcement data in edit mode
  useEffect(() => {
    if (announcement) {
      setTitle(announcement.title || '')
      setContent(announcement.content || '')
      setCategory(announcement.category || 'General')
      setPriority(announcement.priority || 'Normal')
    } else {
      // Reset form in create mode
      resetForm()
    }
  }, [announcement, isOpen])

  const resetForm = () => {
    setTitle('')
    setContent('')
    setCategory('General')
    setPriority('Normal')
    setError('')
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    // Validate
    if (!title.trim()) {
      setError('Title is required')
      return
    }

    if (!content.trim()) {
      setError('Content is required')
      return
    }

    if (title.length > titleMaxLength) {
      setError(`Title cannot exceed ${titleMaxLength} characters`)
      return
    }

    if (content.length > contentMaxLength) {
      setError(`Content cannot exceed ${contentMaxLength} characters`)
      return
    }

    // Submit
    onSubmit({
      title: title.trim(),
      content: content.trim(),
      category,
      priority
    })
  }

  const handleClose = () => {
    if (!isLoading) {
      resetForm()
      onClose()
    }
  }

  const handleBackdropClick = (e) => {
    // Close only if clicking the backdrop, not the modal content
    if (e.target.classList.contains('psyber-modal-overlay')) {
      handleClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="psyber-modal-overlay" onClick={handleBackdropClick}>
      <div className="psyber-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="psyber-modal-header">
          <h3 style={{
            margin: 0,
            fontSize: 'var(--psyber-font-size-2xl)',
            color: 'var(--psyber-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <i className="fas fa-bullhorn"></i>
            {announcement ? 'Edit Announcement' : 'Post New Announcement'}
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--psyber-text-secondary)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '2rem',
              height: '2rem',
              borderRadius: 'var(--psyber-radius-sm)',
              transition: 'var(--psyber-transition-fast)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.background = 'var(--psyber-card-hover)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none'
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          {/* Error Display */}
          {error && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--psyber-error-bg)',
              border: '2px solid var(--psyber-error)',
              borderRadius: 'var(--psyber-radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-exclamation-circle" style={{ fontSize: '1.5rem', color: 'var(--psyber-error)' }}></i>
              <span style={{ color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</span>
            </div>
          )}

          {/* Title Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="title"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-heading"></i> Title *
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter announcement title"
              disabled={isLoading}
              maxLength={titleMaxLength}
              className="psyber-input"
              style={{ width: '100%' }}
              required
            />
            <div style={{
              marginTop: '0.25rem',
              fontSize: 'var(--psyber-font-size-xs)',
              color: title.length > titleMaxLength * 0.9 ? 'var(--psyber-warning)' : 'var(--psyber-text-muted)',
              textAlign: 'right'
            }}>
              {title.length}/{titleMaxLength} characters
            </div>
          </div>

          {/* Content Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="content"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-align-left"></i> Content *
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter announcement content"
              disabled={isLoading}
              maxLength={contentMaxLength}
              className="psyber-input"
              rows={8}
              style={{
                width: '100%',
                resize: 'vertical',
                minHeight: '150px'
              }}
              required
            />
            <div style={{
              marginTop: '0.25rem',
              fontSize: 'var(--psyber-font-size-xs)',
              color: content.length > contentMaxLength * 0.9 ? 'var(--psyber-warning)' : 'var(--psyber-text-muted)',
              textAlign: 'right'
            }}>
              {content.length}/{contentMaxLength} characters
            </div>
          </div>

          {/* Category and Priority Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {/* Category Dropdown */}
            <div>
              <label
                htmlFor="category"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: 'var(--psyber-font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--psyber-text)'
                }}
              >
                <i className="fas fa-tag"></i> Category *
              </label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={isLoading}
                className="psyber-input"
                style={{
                  width: '100%',
                  cursor: 'pointer'
                }}
                required
              >
                {getCategoryOptions().map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Priority Dropdown */}
            <div>
              <label
                htmlFor="priority"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: 'var(--psyber-font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--psyber-text)'
                }}
              >
                <i className="fas fa-exclamation-triangle"></i> Priority *
              </label>
              <select
                id="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                disabled={isLoading}
                className="psyber-input"
                style={{
                  width: '100%',
                  cursor: 'pointer'
                }}
                required
              >
                {getPriorityOptions().map(pri => (
                  <option key={pri} value={pri}>{pri}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Help Text */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '0.75rem',
            background: 'var(--psyber-info-bg)',
            border: '1px solid var(--psyber-info)',
            borderRadius: 'var(--psyber-radius-md)',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <i className="fas fa-info-circle" style={{ color: 'var(--psyber-info)', marginRight: '0.5rem' }}></i>
            <strong>Tip:</strong> Urgent announcements will be highlighted with a red border to grab attention.
          </div>

          {/* Modal Footer */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            paddingTop: '1rem',
            borderTop: '1px solid var(--psyber-card-border)'
          }}>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={announcement ? 'fas fa-save' : 'fas fa-paper-plane'}
              loading={isLoading}
              disabled={isLoading || !title.trim() || !content.trim()}
            >
              {isLoading ? 'Saving...' : (announcement ? 'Update' : 'Post Announcement')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AnnouncementModal
